/**
 * The manual input surface — the way to play with no camera, no permission
 * prompt and no model download, and the way the mapping layer is driven from a
 * desktop. It emits the SAME FeatureFrame the camera emits:
 *
 *   XY mode      x → spread, y → height, press → aperture closes (the pinch),
 *                horizontal micro-motion → roll.
 *   PLATTER mode a circular drag whose ANGLE feeds the scratch rate law
 *                unchanged — the same unwrap/velocity math the wrist drives.
 *
 * The pad has no second hand, so it declares sourceHasPitch: false — degree
 * selection stays wherever it is and never latches. Everything downstream
 * (pipeline, mappers, adapters) is identical for pad, camera and fixture.
 */

import type { FrameCallback, InputSource } from '../input/source';
import type { Features } from '../mapping/types';
import { el } from './controls';

export type PadMode = 'xy' | 'platter';

export interface PadState {
  down: boolean;
  x: number; // 0..1
  y: number; // 0..1, 1 = top
  angle: number; // radians, platter mode
  turns: number;
}

export interface GesturePad extends InputSource {
  readonly root: HTMLElement;
  readonly state: PadState;
  setMode(m: PadMode): void;
}


export function createGesturePad(mode: PadMode = 'xy'): GesturePad {
  const surface = el('div', {
    class: 'pad', tabindex: '0', role: 'application',
    'aria-label': 'Gesture pad — drag to play. Left-right is spread, up-down is level, press to trigger.',
  });
  const ring = el('div', { class: 'pad-ring' });
  const dot = el('div', { class: 'pad-dot' });
  const hint = el('div', { class: 'pad-hint' }, ['drag to play']);
  surface.append(ring, dot, hint);
  const root = el('div', { class: 'pad-wrap' }, [surface]);

  const state: PadState = { down: false, x: 0.5, y: 0.35, angle: 0, turns: 0 };
  let padMode: PadMode = mode;
  let cb: FrameCallback | null = null;
  let raf = 0;
  let last = performance.now();
  let prevRawAngle = 0;
  let lastX = 0.5;
  let rollNow = 0.5;

  function locate(ev: PointerEvent): void {
    const r = surface.getBoundingClientRect();
    const nx = Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width));
    const ny = Math.min(1, Math.max(0, (ev.clientY - r.top) / r.height));
    lastX = state.x;
    state.x = nx;
    state.y = 1 - ny;
    const cx = nx - 0.5;
    const cy = ny - 0.5;
    const a = Math.atan2(cy, cx);
    let d = a - prevRawAngle;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    state.turns += d / (Math.PI * 2);
    prevRawAngle = a;
    state.angle = a;
    dot.style.left = `${nx * 100}%`;
    dot.style.top = `${ny * 100}%`;
  }

  surface.addEventListener('pointerdown', (e) => {
    surface.setPointerCapture(e.pointerId);
    state.down = true;
    const r = surface.getBoundingClientRect();
    prevRawAngle = Math.atan2((e.clientY - r.top) / r.height - 0.5, (e.clientX - r.left) / r.width - 0.5);
    locate(e);
    surface.classList.add('down');
    hint.textContent = padMode === 'platter' ? 'spin' : 'playing';
    e.preventDefault();
  });
  surface.addEventListener('pointermove', (e) => { if (state.down) locate(e); });
  const up = (): void => {
    if (!state.down) return;
    state.down = false;
    surface.classList.remove('down');
    hint.textContent = padMode === 'platter' ? 'spin the platter' : 'drag to play';
  };
  surface.addEventListener('pointerup', up);
  surface.addEventListener('pointercancel', up);
  surface.addEventListener('pointerleave', up);

  // Keyboard parity: a phone instrument still has to be reachable without a
  // pointer, and the debug surface is easier to drive from a desktop keyboard.
  surface.addEventListener('keydown', (e) => {
    const stepK = e.shiftKey ? 0.15 : 0.05;
    if (e.key === 'ArrowUp') state.y = Math.min(1, state.y + stepK);
    else if (e.key === 'ArrowDown') state.y = Math.max(0, state.y - stepK);
    else if (e.key === 'ArrowRight') { state.x = Math.min(1, state.x + stepK); state.angle += 0.35; }
    else if (e.key === 'ArrowLeft') { state.x = Math.max(0, state.x - stepK); state.angle -= 0.35; }
    else if (e.key === ' ' || e.key === 'Enter') { state.down = !state.down; }
    else return;
    dot.style.left = `${state.x * 100}%`;
    dot.style.top = `${(1 - state.y) * 100}%`;
    e.preventDefault();
  });

  function frame(): void {
    raf = requestAnimationFrame(frame);
    const now = performance.now();
    const dt = Math.min(0.1, Math.max(0.001, (now - last) / 1000));
    last = now;
    // Roll is a flick: horizontal micro-motion kicks it off centre, and it
    // decays back to 0.5 (~150 ms) when the pointer rests. It used to freeze
    // at the last delta, parking resonance wherever the final flick left it.
    rollNow += (0.5 + (state.x - lastX) * 6 - rollNow) * Math.min(1, dt / 0.15);
    lastX = state.x;
    const expr: Features = {
      // press closes the pinch; released the hand sits open. The vocabulary's
      // own hysteresis turns this into the trigger — the pad synthesises a
      // measure, never an event.
      aperture: state.down ? 0.2 : 0.8,
      height: state.y,
      spread: state.x,
      roll: Math.min(1, Math.max(0, rollNow)),
      angle: state.angle,
      fistPose: false, // a pointer has no fingers to curl
      present: true,
      confident: true,
    };
    cb?.({ expr, dt, sourceHasPitch: false });
  }

  return {
    id: 'pad',
    root,
    state,
    /** The pad is a pointer surface — a take from it carries no video. */
    videoTracks: () => [],
    dispose() { this.stop(); },
    setMode(m) {
      padMode = m;
      root.dataset.mode = m;
      surface.classList.toggle('platter', m === 'platter');
      hint.textContent = m === 'platter' ? 'spin the platter' : 'drag to play';
      surface.setAttribute(
        'aria-label',
        m === 'platter'
          ? 'Platter — press and spin. Angular velocity is the playback rate; reverse motion plays backwards.'
          : 'Gesture pad — drag to play. Left-right is spread, up-down is level, press to trigger.',
      );
    },
    onFrame(fn) { cb = fn; },
    // Async only to satisfy the shared InputSource contract — the camera needs
    // to await a permission prompt and a model download; the pad starts at once.
    async start() { if (!raf) { last = performance.now(); raf = requestAnimationFrame(frame); } },
    stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } },
  };
}
